/**
 * Custom Cart Recommendations Engine for Revelry Home Goods
 */

class CartRecommendationsCustom extends HTMLElement {
  constructor() {
    super();
    this.FREE_SHIPPING_THRESHOLD = 7500;
    this.UPSELL_MIN_PRICE = 500;
    this.UPSELL_MAX_PRICE = 5000;
    this.RECOMMENDATION_COUNT = 6;
  }

  connectedCallback() {
    // Intentionally empty - initialization handled explicitly via initWithCartData
  }

  async init() {
    try {
      const cartData = this.getCartData();
      if (!cartData || cartData.items.length === 0) return;
      const allProducts = await this.fetchAllProducts();
      if (!allProducts.length) { this.renderFallback(); return; }
      const recommendations = this.scoreAndSort(cartData, allProducts);
      this.render(recommendations);
    } catch (error) {
      console.error('[Revelry Recommendations] Error:', error);
      this.renderFallback();
    }
  }

  async initWithCartData(cartData) {
    try {
      const allProducts = await this.fetchAllProducts();
      if (!allProducts.length) { this.renderFallback(); return; }
      const recommendations = this.scoreAndSort(cartData, allProducts);
      this.render(recommendations);
    } catch (error) {
      console.error('[Revelry Recommendations] Error:', error);
      this.renderFallback();
    }
  }

  getCartData() {
    const el = document.getElementById('cart-recommendations-data');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.error('[Revelry Recommendations] Failed to parse cart data:', e);
      return null;
    }
  }

  async fetchAllProducts() {
    if (!CartRecommendationsCustom.catalogPromise) {
      CartRecommendationsCustom.catalogPromise = window.fetch('/collections/all/products.json?limit=250')
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.json();
        })
        .then((data) => data.products || [])
        .catch((error) => {
          CartRecommendationsCustom.catalogPromise = null;
          console.error('[Revelry Recommendations] Failed to fetch products:', error);
          return [];
        });
    }
    return CartRecommendationsCustom.catalogPromise;
  }

  scoreAndSort(cartData, allProducts) {
    const cartTotal = cartData.cart_total;
    const gap = this.FREE_SHIPPING_THRESHOLD - cartTotal;
    const isUpsellMode = gap <= 0;
    const isCloseToThreshold = !isUpsellMode && gap <= 2000;
    const cartProductIds = new Set(cartData.items.map(i => i.id));

    const artists = [...new Set(cartData.items.map(i => i.artist).filter(Boolean))];
    const vendors = [...new Set(cartData.items.map(i => i.vendor).filter(Boolean))];

    const excludeSet = new Set([
      ...vendors.map(v => v.toLowerCase()),
      ...artists.map(a => a.toLowerCase()),
    ]);

    const cartProductMap = new Map(allProducts.map(p => [p.id, p]));

    const allCartTags = cartData.items.flatMap(item => {
      const product = cartProductMap.get(item.id);
      if (!product) return [];
      const tags = Array.isArray(product.tags)
        ? product.tags
        : (product.tags || '').split(',').map(t => t.trim()).filter(Boolean);
      return tags;
    });

    const affinityTags = [...new Set(
      allCartTags
        .map(t => t.toLowerCase().trim())
        .filter(t => !t.startsWith('collection-') && !excludeSet.has(t))
    )];

    const cartCollections = new Set(
      allCartTags
        .map(t => t.toLowerCase().trim())
        .filter(t => t.startsWith('collection-'))
    );

    const candidates = allProducts
      .filter(p => !cartProductIds.has(p.id) && p.available !== false)
      .map(p => {
        const productTags = Array.isArray(p.tags)
          ? p.tags.map(t => t.trim().toLowerCase())
          : p.tags
            ? p.tags.split(',').map(t => t.trim().toLowerCase())
            : [];

        const price = Math.round(parseFloat(p.variants?.[0]?.price || '0') * 100);
        let priority = null;
        let tagScore = 0;

        if (isUpsellMode) {
          priority = 4;
          const inUpsellRange = price >= this.UPSELL_MIN_PRICE && price <= this.UPSELL_MAX_PRICE;

          if (artists.length > 0 && artists.some(a => productTags.includes(a.toLowerCase()))) {
            priority = 1;
          }
          tagScore = affinityTags.filter(at => 
            productTags.some(pt => pt.includes(at) || at.includes(pt))
          ).length;

          if (tagScore > 0 && priority > 2) priority = 2;
          if (vendors.includes(p.vendor) && priority > 3) priority = 3;
          if (priority === 4 && !inUpsellRange) return null;

        } else if (isCloseToThreshold) {
          const closesGap = price >= gap && price <= gap * 2.5;
          const nearGap = price < gap && price >= gap * 0.5;
          const sharesCollection = productTags.some(t => cartCollections.has(t));

          tagScore = affinityTags.filter(at => 
            productTags.some(pt => pt.includes(at) || at.includes(pt))
          ).length;

          if (sharesCollection && tagScore > 0) {
            priority = 1;
          } else if (sharesCollection) {
            priority = 2;
          } else if (tagScore > 0) {
            priority = 3;
          } else if (closesGap || nearGap) {
            priority = 4;
          } else {
            priority = 5;
          }

        } else {
          priority = 5;

          if (artists.length > 0 && artists.some(a => productTags.includes(a.toLowerCase()))) {
            priority = 1;
          }
          tagScore = affinityTags.filter(at => 
            productTags.some(pt => pt.includes(at) || at.includes(pt))
          ).length;
          if (tagScore > 0 && priority > 2) priority = 2;

          const productCollections = productTags.filter(t => t.startsWith('collection-'));
          if (productCollections.some(t => cartCollections.has(t)) && priority > 3) priority = 3;
          if (vendors.includes(p.vendor) && priority > 4) priority = 4;
        }

        return {
          id: p.id,
          title: p.title,
          handle: p.handle,
          price,
          priceFormatted: '$' + (price / 100).toFixed(2) + ' USD',
          vendor: p.vendor,
          image: p.images?.[0]?.src || '',
          variantId: p.variants?.[0]?.id,
          url: '/products/' + p.handle,
          priority,
          tagScore,
          isUpsellMode,
        };
      })
      .filter(Boolean);

    candidates.sort((a, b) => {
      if (a.priority !== b.priority) return a.priority - b.priority;

      if (isCloseToThreshold) {
        if (a.priority !== b.priority) return a.priority - b.priority;
        const aClosesGap = a.price >= gap && a.price <= gap * 2.5;
        const bClosesGap = b.price >= gap && b.price <= gap * 2.5;
        if (aClosesGap !== bClosesGap) return aClosesGap ? -1 : 1;
        return Math.abs(a.price - gap) - Math.abs(b.price - gap) || b.tagScore - a.tagScore;
      }

      if (a.priority === 2 && b.priority === 2 && a.tagScore !== b.tagScore) {
        return b.tagScore - a.tagScore;
      }

      if (isUpsellMode) {
        return a.price - b.price;
      } else {
        return this.gapPriceSort(a, b, gap);
      }
    });

    return candidates.slice(0, this.RECOMMENDATION_COUNT);
  }

  gapPriceSort(a, b, gap) {
    const aCloses = a.price >= gap;
    const bCloses = b.price >= gap;
    if (aCloses !== bCloses) return aCloses ? -1 : 1;
    if (aCloses && bCloses) return a.price - b.price;
    return Math.abs(a.price - gap) - Math.abs(b.price - gap);
  }

  render(recommendations) {
    if (recommendations.length === 0) {
      this.renderFallback();
      return;
    }

    const grid = this.querySelector('.cart-drawer__recommendations-grid') || this;
    const drawerWidth = this.closest('cart-drawer')?.offsetWidth || window.innerWidth;
    const isNarrow = window.innerWidth <= 767;

    grid.innerHTML = recommendations.map(product => `
      <div class="cart-drawer__recommendation-item" style="
        display: flex;
        flex-direction: ${isNarrow ? 'column' : 'row'};
        gap: ${isNarrow ? '8px' : '12px'};
        padding: 8px 0;
        align-items: flex-start;
        ${isNarrow ? 'width: 120px; flex-shrink: 0;' : ''}
      ">
        <a href="${product.url}" style="${isNarrow ? 'width: 100%; display: block;' : 'flex-shrink: 0;'}">
          <img
            src="${this.resizeImage(product.image, 200)}"
            alt="${this.escapeHtml(product.title)}"
            loading="lazy"
            width="${isNarrow ? '120' : '80'}"
            height="${isNarrow ? '100' : '80'}"
            style="object-fit: cover; border-radius: ${isNarrow ? '4px' : '2px'}; ${isNarrow ? 'width: 100%; height: 100px; display: block;' : ''}"
          >
        </a>
        <div style="${isNarrow ? 'width: 100%;' : 'flex: 1;'} min-width: 0;">
          <a href="${product.url}" class="cart-drawer__rec-title" style="
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
            font-size: 0.875rem;
            line-height: 1.4;
            color: inherit;
            text-decoration: none;
            margin-bottom: 4px;
          ">${this.escapeHtml(product.title)}</a>
          <p style="font-size: 0.875rem; margin: 0 0 8px;">${product.priceFormatted}</p>
          <button
            class="cart-drawer__rec-add-btn"
            data-variant-id="${product.variantId}"
            style="
              background: none;
              border: none;
              padding: 0;
              font-size: 0.8125rem;
              cursor: pointer;
              display: inline-flex;
              align-items: center;
              gap: 4px;
              color: inherit;
              text-decoration: underline;
            "
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></svg>
            Add to cart
          </button>
        </div>
      </div>
    `).join('');

    grid.querySelectorAll('.cart-drawer__rec-add-btn').forEach(btn => {
      btn.addEventListener('click', (e) => this.handleAddToCart(e));
    });
  }

  renderFallback() {
    const url = this.dataset.fallbackUrl;
    if (url) {
      window.fetch(url)
        .then(r => r.text())
        .then(html => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const content = doc.querySelector('.cart-drawer__recommendations-grid');
          if (content) {
            const grid = this.querySelector('.cart-drawer__recommendations-grid') || this;
            grid.innerHTML = content.innerHTML;
          }
        })
        .catch(err => console.error('[Revelry Recommendations] Fallback failed:', err));
    }
  }

  async handleAddToCart(e) {
    const btn = e.currentTarget;
    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    btn.disabled = true;
    const originalHTML = btn.innerHTML;
    btn.textContent = 'Adding...';

    try {
      const response = await window.fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 }),
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const cartResponse = await window.fetch('/cart.js');
      const updatedCart = await cartResponse.json();

      if (typeof window.revelryUpdateCartUI === 'function') {
        window.revelryUpdateCartUI(updatedCart);
      }

      await new Promise(resolve => setTimeout(resolve, 300));
      const sectionResponse = await window.fetch(`/?sections=cart-drawer&t=${Date.now()}`);
      const sections = await sectionResponse.json();

      if (sections['cart-drawer']) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(sections['cart-drawer'], 'text/html');
        const newForm = doc.querySelector('#CartDrawer-Form');
        const currentForm = document.querySelector('#CartDrawer-Form');
        if (newForm && currentForm) currentForm.replaceWith(newForm);
      }

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          customElements.whenDefined('cart-recommendations-custom').then(() => {
            const rec = document.querySelector('cart-recommendations-custom');
            if (rec) {
              rec.initWithCartData({
                cart_total: updatedCart.total_price,
                items: updatedCart.items.map(item => ({
                  id: item.product_id,
                  vendor: item.vendor,
                  artist: item.properties?.artist || null,
                  tags: item.tags || [],
                })),
              });
            }
          });
        });
      });

      btn.disabled = false;
      btn.innerHTML = originalHTML;

    } catch (error) {
      console.error('[Revelry Recommendations] Add to cart failed:', error);
      btn.disabled = false;
      btn.innerHTML = originalHTML;
    }
  }

  resizeImage(src, size) {
    if (!src) return '';
    return src.replace(/(\.\w+)(\?|$)/, `_${size}x$1$2`);
  }

  escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
}

if (!customElements.get('cart-recommendations-custom')) {
  customElements.define('cart-recommendations-custom', CartRecommendationsCustom);
}
